import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { useAppAuth } from '@/contexts/app-auth.context'
import {
  directoryApiClient,
  type RoleWithMembers,
  type Staff,
} from '@/lib/api/directory.client'
import { directoryAdminApiClient } from '@/lib/api/directory-admin.client'

interface DirectoryContextValue {
  roles: RoleWithMembers[]
  staff: Staff[]
  loading: boolean
  refresh: () => Promise<void>
}

const DirectoryContext = createContext<DirectoryContextValue | undefined>(
  undefined,
)

export function DirectoryProvider({ children }: { children: ReactNode }) {
  const { currentCompany, isSuperAdmin } = useAppAuth()
  const [roles, setRoles] = useState<RoleWithMembers[]>([])
  const [staff, setStaff] = useState<Staff[]>([])
  const [loading, setLoading] = useState(true)

  const refresh = async () => {
    if (!currentCompany?.id) {
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      const [rolesList, staffList] = await Promise.all([
        isSuperAdmin
          ? directoryAdminApiClient.listRoles(currentCompany.id)
          : directoryApiClient.listRoles(),
        isSuperAdmin
          ? directoryAdminApiClient.listStaff(currentCompany.id)
          : directoryApiClient.listStaff(),
      ])
      setRoles(Array.isArray(rolesList) ? rolesList : [])
      setStaff(Array.isArray(staffList) ? staffList : [])
    } catch (error) {
      console.error('Failed to load directory', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (currentCompany?.id) refresh()
  }, [currentCompany?.id])

  return (
    <DirectoryContext.Provider value={{ roles, staff, loading, refresh }}>
      {children}
    </DirectoryContext.Provider>
  )
}

export function useDirectory(): DirectoryContextValue {
  const ctx = useContext(DirectoryContext)
  if (!ctx) {
    throw new Error('useDirectory must be used within a DirectoryProvider')
  }
  return ctx
}
